import { cx } from '@emotion/css';
import { Trans, useTranslation } from '@sortlist-frontend/translation/ssr';
import dynamic from 'next/dynamic';

import { DirectBriefingButton } from '_components/Briefing/versions/DirectBriefing/button';
import { CompareButtonProps } from '_components/Comparator/Button';
import { usePublicAppContext } from '_core/context/public-app-context';
import { useComparedAgencies } from '_core/hooks/use-compared-agencies';
import { agencyProfileConfig } from '_features/AgencyProfile/config';
import { useGetAgencyProfile } from '_features/AgencyProfile/hooks/useGetAgencyProfile';

const CompareButton = dynamic<CompareButtonProps>(
  () => import('_components/Comparator/Button').then((mod) => mod.CompareButton),
  { ssr: false },
);

const defaultSquareImage = '/_img/default/default-image-landscape.svg';

type Props = {
  className?: string;
};

export const BottomBar = (props: Props) => {
  const { className } = props;
  const { t } = useTranslation(agencyProfileConfig.i18nNamespaces);
  const { comparedAgencies, setComparedAgencies } = useComparedAgencies();
  const { locale, pageData, showSnackbar } = usePublicAppContext();
  const { agencySlug } = pageData as { agencySlug: string };
  const { data: { agency } = {} } = useGetAgencyProfile({ agencySlug, locale: locale as string });

  const agencyName = agency?.name;
  const logo = agency?.logoUrl ?? defaultSquareImage;

  return (
    <div className={cx(className, 'fixed width-100 py-3 bg-secondary-50 shadow-2 z-50 bottom-0 left-0')}>
      <div className="container-lg layout-row layout-align-start-center">
        <div className="hide-xs layout-column layout-align-center-start mr-4 flex-1">
          <span className="small bold">{t('agency:profile.team.cta.title', { name: agencyName })}</span>
          <span className="small">{t('agency:profile.team.cta.subtitle')}</span>
        </div>
        <CompareButton
          showSnackbar={showSnackbar}
          className="flex-xs"
          page="profile-bottom"
          agency={{ logo, slug: agencySlug, name: agencyName, external_id: agency?.uuid }}
          searchContext={undefined}
          comparing={comparedAgencies.some((compared) => agencySlug === compared.slug)}
          onChange={setComparedAgencies}
          comparedAgencies={comparedAgencies}
        />
        <DirectBriefingButton
          page="agency"
          className="mb-4 mt-2 flex-33"
          cta="about-panel"
          truncate={true}
          agencySlug={agencySlug}
          size="md"
          buttonStyle="primary"
          buttonVariant="raised"
          label={<Trans i18nKey={'agency:profile.about.contactCta'}>Contact {{ agencyName }}</Trans>}
        />
      </div>
    </div>
  );
};
